import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const gatedRedirect = location.state?.gated;
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      // Redirect based on role
      if (user.role === 'admin' || user.role === 'superadmin') {
        navigate('/admin');
      } else if (user.role === 'coach') {
        navigate('/coach');
      } else {
        // Clients land on educational content while the dashboard is being rebuilt
        navigate('/the-approach');
      }
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      // Redirect based on role
      if (userData.role === 'admin' || userData.role === 'superadmin') {
        navigate('/admin');
      } else if (userData.role === 'coach') {
        navigate('/coach');
      } else {
        // Clients land on educational content while the dashboard is being rebuilt
        navigate('/the-approach');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0E9] flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
              alt="Quiet Mind Golf" 
              className="h-12 w-auto"
            />
            <span className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A]">
              Quiet Mind Golf
            </span>
          </Link>

          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">
            Welcome Back
          </h1>
          <p className="text-[#57534E] mb-6">
            Sign in to access your free educational content
          </p>

          {gatedRedirect ? (
            <div className="mb-6 p-4 bg-[#BC562B]/10 border border-[#BC562B]/40 rounded-lg" data-testid="gated-content-message">
              <p className="text-sm text-[#1A1A1A] font-semibold mb-1">
                Almost there — this content is free, you just need an account.
              </p>
              <p className="text-sm text-[#57534E] leading-relaxed">
                Sign in below or <Link to="/register" className="text-[#2C5530] font-semibold underline">create a free account</Link> to unlock the QMG Blueprint, Mind Factor, and Acceptance vs. Resistance.
              </p>
              <p className="text-xs text-[#737373] mt-2">
                Are you a coach? <Link to="/coach-login" className="underline font-medium">Sign in here</Link>
              </p>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-[#2C5530]/5 border border-[#2C5530]/30 rounded-lg" data-testid="free-access-banner">
              <p className="text-sm text-[#1A1A1A] leading-relaxed">
                <span className="font-semibold text-[#2C5530]">All content is free.</span> A free account just lets you unlock the educational pages and receive occasional mental-game tips. <Link to="/register" className="text-[#2C5530] font-semibold underline">Create one in 30 seconds.</Link>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-white"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-white pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1A1A1A]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="mt-4 text-right">
              <Link 
                to="/forgot-password" 
                className="text-sm text-[#737373] hover:text-[#2C5530]"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <p className="mt-8 text-center text-[#57534E]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#2C5530] font-semibold hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>

          <Link
            to="/"
            className="block mt-6 text-center text-[#737373] hover:text-[#1A1A1A] text-sm"
            data-testid="back-home-link"
          >
            ← Back to website
          </Link>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://customer-assets.emergentagent.com/job_55fcb7b6-fbff-4bc2-8a70-17885eded302/artifacts/ag20k6m5_putting.jpeg"
          alt="Golfer in focused state"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2C5530]/40"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium mb-4">
            "Track your progress, master your mind."
          </blockquote>
          <p className="text-white/80">Your personal mental game dashboard awaits</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
